import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  Avatar,
  Chip,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { SectionTitle } from "@components/PhotoFilters/SectionTitle";
import { useGetCharactersQuery } from "@store/characterApi";
import { Character } from "@models/character";
import { formatName, formatNameWithRelation } from "@utils/formatting.util";
import { Controller, UseFormReturn } from "react-hook-form";
import { PhotoSearchFilters } from "@store/photoApi";

const CharacterItem: FC<{
  character: Character;
}> = ({ character }) => {
  const { t } = useTranslation();
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Avatar
        src={character.mainFace.faceUrl}
        variant="circular"
        sx={{ width: "24px", height: "24px" }}
      />

      <Typography>
        {character.person
          ? formatNameWithRelation(character.person)
          : t("albums.addPhotosDialog.filters.people.notAssigned")}
      </Typography>
    </Stack>
  );
};

const PeopleFilters: FC<{
  patientId: string;
  form: UseFormReturn<PhotoSearchFilters>;
}> = ({ patientId, form: { control } }) => {
  const { t } = useTranslation();
  const { data: characters } = useGetCharactersQuery({ patientId });

  const filteredCharacters = useMemo(() => {
    return characters?.data.filter((character) => !!character.person) || [];
  }, [characters]);

  return (
    <Stack>
      <SectionTitle>
        {t("albums.addPhotosDialog.filters.people.title")}
      </SectionTitle>

      <Controller
        name="characterIds"
        control={control}
        render={({ field: { onChange } }) => (
          <Autocomplete
            multiple
            options={filteredCharacters}
            getOptionLabel={(character) => formatName(character.person)}
            onChange={(e, characters) =>
              onChange(characters.map(({ id }) => id))
            }
            defaultValue={[]}
            renderTags={(characters, getTagProps) => {
              return characters.map((character, index) => {
                const { key, ...props } = getTagProps({ index });
                return (
                  <Chip
                    avatar={
                      <Avatar
                        src={character.mainFace.faceUrl}
                        variant="circular"
                        sx={{ width: "22px", height: "22px" }}
                      />
                    }
                    label={formatName(character.person)}
                    {...props}
                    key={key}
                  />
                );
              });
            }}
            renderOption={({ key, ...props }, character) => {
              return (
                <li {...props} key={key}>
                  <CharacterItem character={character} />
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                variant="outlined"
                placeholder={t(
                  "albums.addPhotosDialog.filters.people.input.placeholder",
                )}
              />
            )}
          />
        )}
      />
    </Stack>
  );
};

export default PeopleFilters;
